import { Controller } from '@hotwired/stimulus'

const CHIP_ACTIVE_CLASSES = [
  'bg-emerald-50',
]

const FILTER_DEBOUNCE_MS = 150

export default class extends Controller {
  static targets = [
    'header',
    'searchInput',
    'searchWrap',
    'searchToggle',
    'searchPanel',
    'categoryChips',
    'card',
    'grid',
    'emptyState',
  ]

  static values = {
    initialCategory: { type: String, default: 'all' },
    initialSearch: { type: String, default: '' },
    categories: { type: Array, default: [] },
  }

  connect() {
    this.activeCategory = this.validCategory(this.initialCategoryValue)
    this.filterTimeout = null
    this.searchOpen = false
    this.boundPopstate = this.handlePopstate.bind(this)
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this)
    window.addEventListener('popstate', this.boundPopstate)
    document.addEventListener('click', this.boundDocumentClick)
    document.addEventListener('keydown', this.boundDocumentKeydown)
    if (this.initialSearchValue && this.hasSearchInputTarget) {
      this.searchInputTarget.value = this.initialSearchValue
      this.openSearch()
    }
    this.updateCategoryChipStyles()
    this.applyFilter()
    this.scrollToFirstVisibleCard()
    this.scrollActiveChipIntoView()
  }

  disconnect() {
    window.removeEventListener('popstate', this.boundPopstate)
    document.removeEventListener('click', this.boundDocumentClick)
    document.removeEventListener('keydown', this.boundDocumentKeydown)
    clearTimeout(this.filterTimeout)
  }

  selectCategory(event) {
    const category = this.validCategory(event.currentTarget.dataset.category || 'all')
    if (category === this.activeCategory) return

    this.activeCategory = category
    this.syncCategoryUrl()
    this.updateCategoryChipStyles()
    this.applyFilter()
    this.scrollActiveChipIntoView(event.currentTarget)
  }

  filter() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this.applyFilter(), FILTER_DEBOUNCE_MS)
  }

  applyFilter() {
    const query = this.normalizeSearch(
      this.hasSearchInputTarget ? this.searchInputTarget.value : '',
    )
    const searchActive = Boolean(query)
    let visibleCount = 0

    this.cardTargets.forEach((card) => {
      const categories = (card.dataset.categories || card.dataset.category || '')
        .split(/\s+/)
        .filter(Boolean)
      const searchText = card.dataset.searchText || ''
      const categoryMatch =
        searchActive ||
        this.activeCategory === 'all' ||
        categories.includes(this.activeCategory)
      const searchMatch = !query || searchText.includes(query)
      const visible = categoryMatch && searchMatch

      card.classList.toggle('hidden', !visible)
      card.toggleAttribute('hidden', !visible)
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('hidden', visibleCount > 0)
    }

    if (this.hasGridTarget) {
      this.gridTarget.classList.toggle('hidden', visibleCount === 0)
      this.gridTarget.toggleAttribute('hidden', visibleCount === 0)
    }

    this.updateSearchUI()
  }

  toggleSearch(event) {
    event?.stopPropagation()
    if (this.searchOpen) {
      this.closeSearchPanel({ focusToggle: true })
    } else {
      this.openSearch()
    }
  }

  openSearch() {
    this.searchOpen = true
    this.updateSearchUI()
    requestAnimationFrame(() => this.searchInputTarget?.focus())
  }

  closeSearch(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.closeSearchPanel()
  }

  closeSearchPanel({ focusToggle = false } = {}) {
    this.searchOpen = false
    this.updateSearchUI()
    if (focusToggle && this.hasSearchToggleTarget) {
      this.searchToggleTarget.focus()
    }
  }

  updateSearchUI() {
    if (this.hasSearchPanelTarget) {
      this.searchPanelTarget.classList.toggle('hidden', !this.searchOpen)
      this.searchPanelTarget.classList.toggle('flex', this.searchOpen)
    }

    if (this.hasSearchToggleTarget) {
      this.searchToggleTarget.setAttribute('aria-expanded', String(this.searchOpen))
      const hasQuery = Boolean(this.searchInputTarget?.value.trim())
      this.searchToggleTarget.classList.toggle('bg-slate-100', this.searchOpen || hasQuery)
    }

    if (this.hasHeaderTarget) {
      const isMobile = window.matchMedia('(max-width: 639px)').matches
      this.headerTarget.classList.toggle(
        'char-playground-header--search-open',
        isMobile && this.searchOpen,
      )
    }
  }

  handleDocumentClick(event) {
    if (
      this.searchOpen &&
      this.hasSearchWrapTarget &&
      !this.searchWrapTarget.contains(event.target)
    ) {
      this.closeSearchPanel()
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape' && this.searchOpen) {
      this.closeSearchPanel()
    }
  }

  clearFilters() {
    this.activeCategory = 'all'
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }
    this.syncCategoryUrl()
    this.updateCategoryChipStyles()
    this.applyFilter()
    this.updateSearchUI()
    this.openSearch()
  }

  clearSearchKeydown(event) {
    if (event.key !== 'Escape') return

    if (this.hasSearchInputTarget && this.searchInputTarget.value) {
      event.preventDefault()
      event.stopPropagation()
      this.searchInputTarget.value = ''
      this.applyFilter()
      return
    }

    if (this.searchOpen) {
      event.preventDefault()
      event.stopPropagation()
      this.closeSearchPanel({ focusToggle: true })
      return
    }

    if (this.activeCategory !== 'all') {
      event.preventDefault()
      this.clearFilters()
    }
  }

  handlePopstate() {
    const url = new URL(window.location.href)
    const category = url.searchParams.get('category') || 'all'
    const search = url.searchParams.get('q') || ''
    this.activeCategory = this.validCategory(category)
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = search
    }
    if (search) {
      this.openSearch()
    } else {
      this.closeSearchPanel()
    }
    this.updateCategoryChipStyles()
    this.applyFilter()
  }

  syncCategoryUrl() {
    const url = new URL(window.location.href)
    if (this.activeCategory === 'all') {
      url.searchParams.delete('category')
    } else {
      url.searchParams.set('category', this.activeCategory)
    }
    window.history.replaceState({}, '', url)
  }

  updateCategoryChipStyles() {
    if (!this.hasCategoryChipsTarget) return

    this.categoryChipsTarget.querySelectorAll('[data-category]').forEach((chip) => {
      const active = chip.dataset.category === this.activeCategory
      CHIP_ACTIVE_CLASSES.forEach((className) => {
        chip.classList.toggle(className, active)
      })
      chip.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
  }

  validCategory(category) {
    const id = (category || 'all').toString()
    const allowed = this.categoriesValue || []
    return allowed.includes(id) ? id : 'all'
  }

  normalizeSearch(value) {
    return value.trim().toLocaleLowerCase().normalize('NFKC')
  }

  scrollToFirstVisibleCard() {
    if (!this.initialSearchValue) return

    const card = this.cardTargets.find((entry) => !entry.classList.contains('hidden'))
    if (!card) return

    requestAnimationFrame(() => {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  scrollActiveChipIntoView(chip = null) {
    if (!window.matchMedia('(max-width: 639px)').matches) return
    if (!this.hasCategoryChipsTarget) return

    const activeChip =
      chip ||
      this.categoryChipsTarget.querySelector('[aria-pressed="true"]') ||
      this.categoryChipsTarget.querySelector('[aria-current="page"]')

    if (!activeChip) return

    requestAnimationFrame(() => {
      activeChip.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    })
  }
}
